/**
 * Checkout Modal Component
 *
 * Multi-step checkout flow for OBJKT cart purchases:
 * 1. Validating - Checking which listings are still available
 * 2. Ready - Show valid/invalid items, confirm purchase
 * 3. Signing - User signing the transaction
 * 4. Confirming - Transaction being confirmed on-chain
 * 5. Success/Partial Failure - Show results
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { useWallet } from '@/hooks/useWallet';
import { useBatchPurchase } from '@/hooks/useBatchPurchase';
import { createCheckoutIntent } from '@/lib/objkt-api';
import { API_BASE_URL } from '@/lib/config';
import { Confetti } from './Confetti';
import type {
  CheckoutStatus,
  CheckoutIntentResponse,
  InvalidItem,
} from '@/lib/types/cart';

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CheckoutModal({ isOpen, onClose }: CheckoutModalProps) {
  const navigate = useNavigate();
  const { items, removeItem, clearCart } = useCart();
  const { address, tezos } = useWallet();
  const { executeBatch, status: purchaseStatus, error: purchaseError, result: purchaseResult, reset: resetPurchase } = useBatchPurchase();

  const [status, setStatus] = useState<CheckoutStatus>('idle');
  const [intent, setIntent] = useState<CheckoutIntentResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);

  // Validate cart when modal opens
  useEffect(() => {
    if (isOpen && items.length > 0 && address) {
      validateCart();
    }
    return () => {
      if (!isOpen) {
        setStatus('idle');
        setIntent(null);
        setError(null);
        resetPurchase();
      }
    };
  }, [isOpen]);

  const validateCart = async () => {
    if (!address || items.length === 0) return;

    setStatus('validating');
    setError(null);

    try {
      const response = await createCheckoutIntent(
        address,
        items.map((item) => ({ listingId: item.listingId }))
      );
      setIntent(response);
      setStatus('ready');
    } catch (err) {
      console.error('[Checkout] Validation failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to validate cart');
      setStatus('error');
    }
  };

  const handlePurchase = useCallback(async () => {
    if (!intent || !tezos || !address || intent.validItems.length === 0) return;

    setStatus('signing');
    setError(null);

    try {
      await executeBatch(intent.validItems, intent.intentId);
      // Status will be updated by useBatchPurchase hook
    } catch (err) {
      console.error('[Checkout] Purchase failed:', err);
      setError(err instanceof Error ? err.message : 'Purchase failed');
      setStatus('error');
    }
  }, [intent, tezos, address, executeBatch]);

  // Sync status from purchase hook
  useEffect(() => {
    if (purchaseStatus === 'signing') {
      setStatus('signing');
    } else if (purchaseStatus === 'confirming') {
      setStatus('confirming');
    } else if (purchaseStatus === 'success') {
      setStatus('success');
      setShowConfetti(true);
      // Clear the entire cart on successful purchase
      clearCart();
    } else if (purchaseStatus === 'partial_success') {
      setStatus('partial_failure');
      setShowConfetti(true);
      // Remove only successful items from cart
      if (purchaseResult?.successfulItems) {
        purchaseResult.successfulItems.forEach((item) => {
          removeItem(item.listingId);
        });
      }
    } else if (purchaseStatus === 'error') {
      setStatus('error');
      setError(purchaseError || 'Transaction failed');
    }
  }, [purchaseStatus, purchaseResult, purchaseError, removeItem]);

  const handleRemoveInvalid = () => {
    if (!intent) return;
    intent.invalidItems.forEach((item) => {
      removeItem(item.listingId);
    });
    // Re-validate after removal
    validateCart();
  };

  const handleRetry = () => {
    resetPurchase();
    validateCart();
  };

  const handleClose = () => {
    if (status === 'signing' || status === 'confirming') {
      // Don't close during transaction
      return;
    }
    setShowConfetti(false);
    onClose();
  };

  // Navigation handlers for victory screen
  const handleGoBack = () => {
    setShowConfetti(false);
    onClose();
    // Already on previous path since we used a modal
  };

  const handleViewCollection = () => {
    setShowConfetti(false);
    onClose();
    navigate('/my-collection');
  };

  const handleKeepBrowsing = () => {
    setShowConfetti(false);
    onClose();
    navigate('/collections');
  };

  const getImageUrl = (uri: string | null | undefined) => {
    if (!uri) return '/placeholder-nft.svg';
    if (uri.startsWith('ipfs://')) {
      const cid = uri.replace('ipfs://', '');
      return `${API_BASE_URL}/api/thumb/${cid}`;
    }
    return uri;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Confetti Effect for Victory */}
      <Confetti active={showConfetti} />

      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {status === 'validating' && 'Validating Cart...'}
              {status === 'ready' && 'Confirm Purchase'}
              {status === 'signing' && 'Sign Transaction'}
              {status === 'confirming' && 'Confirming...'}
              {status === 'success' && 'Purchase Complete!'}
              {status === 'partial_failure' && 'Partial Success'}
              {status === 'error' && 'Error'}
            </h2>
            {status !== 'signing' && status !== 'confirming' && (
              <button
                onClick={handleClose}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* Validating State */}
            {status === 'validating' && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mb-4" />
                <p className="text-gray-600 dark:text-gray-400">
                  Checking listing availability...
                </p>
              </div>
            )}

            {/* Ready State */}
            {status === 'ready' && intent && (
              <div className="space-y-4">
                {/* Invalid Items Warning */}
                {intent.invalidItems.length > 0 && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
                    <h3 className="font-medium text-amber-800 dark:text-amber-200 mb-2">
                      {intent.invalidItems.length} item(s) unavailable
                    </h3>
                    <ul className="space-y-2 mb-3">
                      {intent.invalidItems.map((item) => (
                        <li key={item.listingId} className="text-sm text-amber-700 dark:text-amber-300">
                          <span className="font-medium">{item.tokenName || `Listing ${item.listingId}`}</span>
                          <span className="text-amber-600 dark:text-amber-400"> - {getReasonText(item.reason)}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={handleRemoveInvalid}
                      className="text-sm text-amber-700 dark:text-amber-300 underline hover:no-underline"
                    >
                      Remove unavailable items from cart
                    </button>
                  </div>
                )}

                {/* Valid Items */}
                {intent.validItems.length > 0 && (
                  <>
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      {intent.validItems.length} item(s) to purchase
                    </h3>
                    <ul className="space-y-2">
                      {intent.validItems.map((item) => (
                        <li
                          key={item.listingId}
                          className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg"
                        >
                          <div className="w-10 h-10 rounded overflow-hidden bg-gray-200 dark:bg-gray-700 flex-shrink-0">
                            <img
                              src={getImageUrl(item.thumbnailUri || item.displayUri)}
                              alt={item.tokenName || `Token #${item.tokenId}`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = '/placeholder-nft.svg';
                              }}
                            />
                          </div>
                          <span className="flex-1 truncate text-gray-900 dark:text-white">
                            {item.tokenName || `#${item.tokenId}`}
                          </span>
                          <span className="text-blue-600 dark:text-blue-400 font-medium">
                            {item.priceXtz} XTZ
                          </span>
                        </li>
                      ))}
                    </ul>

                    {/* Totals */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-2">
                      <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                        <span>Items Total</span>
                        <span>{formatMutez(intent.totals.itemsPriceMutez)} XTZ</span>
                      </div>
                      {intent.totals.marketplaceFeeMutez && (
                        <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                          <span>Marketplace Fee (1%)</span>
                          <span>{formatMutez(intent.totals.marketplaceFeeMutez)} XTZ</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                        <span>Estimated Gas</span>
                        <span>~{formatMutez(intent.totals.estimatedGasMutez || intent.totals.estimatedFeeMutez || '0')} XTZ</span>
                      </div>
                      <div className="flex justify-between font-semibold text-gray-900 dark:text-white pt-2 border-t border-gray-200 dark:border-gray-700">
                        <span>Total</span>
                        <span>{formatMutez(intent.totals.totalRequiredMutez)} XTZ</span>
                      </div>
                    </div>
                  </>
                )}

                {intent.validItems.length === 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <p>No valid items to purchase.</p>
                    <p className="text-sm mt-2">All items in your cart are unavailable.</p>
                  </div>
                )}
              </div>
            )}

            {/* Signing State */}
            {status === 'signing' && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-pulse rounded-full h-16 w-16 bg-blue-100 dark:bg-blue-900 flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </div>
                <p className="text-gray-900 dark:text-white font-medium">
                  Please sign in your wallet
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Confirm the transaction to purchase {intent?.validItems.length} item(s)
                </p>
              </div>
            )}

            {/* Confirming State */}
            {status === 'confirming' && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mb-4" />
                <p className="text-gray-900 dark:text-white font-medium">
                  Confirming on blockchain...
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  This may take up to 30 seconds
                </p>
              </div>
            )}

            {/* Success State - Victory Screen */}
            {status === 'success' && purchaseResult && (
              <div className="animate-scale-in">
                {/* Victory Header */}
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 animate-victory-glow mb-4">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                    Congratulations!
                  </h3>
                  <p className="text-emerald-600 dark:text-emerald-400 font-medium">
                    {purchaseResult.successfulItems.length === 1
                      ? 'You got the NFT!'
                      : `You got ${purchaseResult.successfulItems.length} NFTs!`}
                  </p>
                </div>

                {/* Purchased Items Showcase */}
                <div className="mb-6">
                  <div className={`grid gap-3 ${
                    purchaseResult.successfulItems.length === 1
                      ? 'grid-cols-1 max-w-[200px] mx-auto'
                      : purchaseResult.successfulItems.length === 2
                        ? 'grid-cols-2 max-w-[280px] mx-auto'
                        : 'grid-cols-3'
                  }`}>
                    {purchaseResult.successfulItems.map((item, index) => (
                      <div
                        key={item.listingId}
                        className="animate-fade-in-up rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800 shadow-lg"
                        style={{ animationDelay: `${index * 0.1}s` }}
                      >
                        <div className="aspect-square relative">
                          <img
                            src={getImageUrl(item.thumbnailUri || item.displayUri)}
                            alt={item.tokenName || `Token #${item.tokenId}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = '/placeholder-nft.svg';
                            }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                          <div className="absolute bottom-0 left-0 right-0 p-2">
                            <p className="text-white text-xs font-medium truncate">
                              {item.tokenName || `#${item.tokenId}`}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Total Spent */}
                <div className="text-center mb-4 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total Spent</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {formatMutez(purchaseResult.totalSpent || '0')} XTZ
                  </p>
                </div>

                {/* Transaction Link */}
                {purchaseResult.operationHash && (
                  <div className="text-center mb-4">
                    <a
                      href={`https://tzkt.io/${purchaseResult.operationHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      View Transaction on TzKT
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Partial Failure State */}
            {status === 'partial_failure' && purchaseResult && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                  <div className="rounded-full h-10 w-10 bg-amber-100 dark:bg-amber-900 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-amber-800 dark:text-amber-200">
                      Partial Success
                    </p>
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      {purchaseResult.successfulItems.length} succeeded, {purchaseResult.failedItems.length} failed
                    </p>
                  </div>
                </div>

                {purchaseResult.failedItems.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Failed Items</h4>
                    <ul className="space-y-2">
                      {purchaseResult.failedItems.map((item) => (
                        <li
                          key={item.listingId}
                          className="text-sm text-gray-600 dark:text-gray-400 flex justify-between"
                        >
                          <span>{item.tokenName || `Listing ${item.listingId}`}</span>
                          <span className="text-red-500">{item.reason}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={handleRetry}
                      className="mt-4 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Retry failed items →
                    </button>
                  </div>
                )}

                {purchaseResult.operationHash && (
                  <a
                    href={`https://tzkt.io/${purchaseResult.operationHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline block text-center"
                  >
                    View successful transaction on TzKT →
                  </a>
                )}
              </div>
            )}

            {/* Error State */}
            {status === 'error' && (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="rounded-full h-16 w-16 bg-red-100 dark:bg-red-900 flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <p className="text-gray-900 dark:text-white font-medium">
                  Something went wrong
                </p>
                <p className="text-sm text-red-500 dark:text-red-400 mt-2 text-center max-w-xs">
                  {error || 'An unexpected error occurred'}
                </p>
                <button
                  onClick={handleRetry}
                  className="mt-4 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Try again
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          {status === 'ready' && intent && intent.validItems.length > 0 && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handlePurchase}
                className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
              >
                Purchase for {formatMutez(intent.totals.totalRequiredMutez)} XTZ
              </button>
            </div>
          )}

          {(status === 'success' || status === 'partial_failure') && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
              {/* Primary Action - View My Collection */}
              <button
                onClick={handleViewCollection}
                className="w-full py-3 px-4 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                View My Collection
              </button>
              {/* Secondary Actions */}
              <div className="flex gap-3">
                <button
                  onClick={handleGoBack}
                  className="flex-1 py-2.5 px-4 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition-colors"
                >
                  Go Back
                </button>
                <button
                  onClick={handleKeepBrowsing}
                  className="flex-1 py-2.5 px-4 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition-colors"
                >
                  Keep Browsing
                </button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex gap-3">
              <button
                onClick={handleClose}
                className="flex-1 py-3 px-4 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-900 dark:text-white font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRetry}
                className="flex-1 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function getReasonText(reason: InvalidItem['reason']): string {
  switch (reason) {
    case 'sold':
      return 'Already sold';
    case 'cancelled':
      return 'Listing cancelled';
    case 'price_changed':
      return 'Price changed';
    case 'not_found':
      return 'Listing not found';
    case 'error':
      return 'Validation error';
    default:
      return 'Unavailable';
  }
}

function formatMutez(mutez: string): string {
  const value = BigInt(mutez);
  const xtz = Number(value) / 1_000_000;
  return xtz.toFixed(2);
}
